import React, { useEffect, useRef } from "react";
import { Outlet } from "react-router-dom";
import { Logo } from "@/components/atoms/Logo";
import { useThemeStore } from "@/store/themeStore";

export const AuthLayout: React.FC = () => {
  const setLightLocked = useThemeStore((s) => s.setLightLocked);

  // Auth pages match the Keycloak login, which has no dark mode — hold them in
  // light mode without touching the saved preference.
  useEffect(() => {
    setLightLocked(true);
    return () => setLightLocked(false);
  }, [setLightLocked]);

  const bgRef = useRef<HTMLDivElement>(null);
  const textRef = useRef<HTMLDivElement>(null);

  // Cursor-driven parallax: the artwork drifts against the pointer and the copy
  // moves slightly with it, so the two read as separate depth layers.
  const handleMove = (e: React.MouseEvent<HTMLDivElement>) => {
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    const r = e.currentTarget.getBoundingClientRect();
    const x = (e.clientX - r.left) / r.width - 0.5;
    const y = (e.clientY - r.top) / r.height - 0.5;
    bgRef.current?.style.setProperty("transform", `translate3d(${-x * 28}px, ${-y * 20}px, 0)`);
    textRef.current?.style.setProperty("transform", `translate3d(${x * 10}px, ${y * 8}px, 0)`);
  };
  const handleLeave = () => {
    bgRef.current?.style.removeProperty("transform");
    textRef.current?.style.removeProperty("transform");
  };

  return (
    <div className="min-h-screen flex p-4 lg:p-6 bg-white font-sans text-bridge-text">
      {/* Left Pane */}
      <div
        onMouseMove={handleMove}
        onMouseLeave={handleLeave}
        className="hidden lg:flex w-1/2 rounded-[32px] p-12 pb-[5vh] flex-col justify-end relative overflow-hidden bg-bridge-light-gray"
      >
        <div
          ref={bgRef}
          className="absolute inset-0 transition-transform duration-300 ease-out motion-reduce:transition-none"
        >
          <div className="absolute inset-0 bg-[url('/auth-bg.svg')] bg-contain bg-center bg-no-repeat animate-bgSettle motion-reduce:animate-none" />
        </div>
        <div
          ref={textRef}
          className="max-w-lg mx-auto text-center mb-12 relative z-10 transition-transform duration-300 ease-out motion-reduce:transition-none"
        >
          <div className="animate-riseIn motion-reduce:animate-none [animation-delay:350ms]">
          <h2 className="text-lg font-bold mb-4 tracking-wider uppercase text-gray-900">Connect with the team</h2>
          <p className="text-gray-700 leading-relaxed text-sm">
            <span className="font-semibold text-[#00a3ff]">Bridge AI</span> is your agents' assistant, managing all your leads 24/7 via WhatsApp & Email and your website, qualifying them up to your standard, booking them into the right negotiator's diary, factoring the travel time, and cross-selling other properties from your portfolio.
          </p>
          </div>
        </div>
      </div>

      {/* Right Pane */}
      <div className="flex-1 flex flex-col px-4 sm:px-12 lg:px-24 xl:px-32">
        <div className="flex-1 flex flex-col justify-center w-full max-w-md mx-auto animate-riseIn motion-reduce:animate-none [animation-delay:150ms]">
          <div className="flex justify-center mb-8">
            <Logo size={56} />
          </div>
          <Outlet />
        </div>

        <div className="w-full max-w-md mx-auto pb-2 pt-8 text-center">
          <p className="text-[11px] font-semibold tracking-[0.18em] text-gray-400 uppercase mb-2">
            Trusted by UK Agencies
          </p>
        </div>
      </div>
    </div>
  );
};
